import slug from '@teko/slug';
import Api from '@teko/api';
import { __rest } from 'tslib';
import useSWR, { useSWRInfinite } from 'swr';

const cleanObject = (object) => {
    const newObj = {};
    Object.keys(object).forEach((key) => {
        if (object[key] || object[key] === false || object[key] === 0) {
            newObj[key] = object[key];
        }
    });
    return newObj;
};
const normalizePageBuilderToDiscoveryParams = (params) => {
    var _a, _b, _c, _d;
    if (!params)
        return {};
    const attributes = params['attributes'] || [];
    const priceGteFromPageBuilder = (_b = (_a = params === null || params === void 0 ? void 0 : params.prices) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.from;
    const priceLteFromPageBuilder = (_d = (_c = params === null || params === void 0 ? void 0 : params.prices) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.to;
    if (!params['price_gte'] && priceGteFromPageBuilder)
        params['price_gte'] = priceGteFromPageBuilder;
    if (!params['price_lte'] && priceLteFromPageBuilder)
        params['price_lte'] = priceLteFromPageBuilder;
    attributes.forEach((element) => {
        const key = `attributes.${element.code}`;
        const value = element.optionIds && element.optionIds.toString();
        params[key] = value;
    });
    params['attributes'] = [];
    return cleanObject(params);
};
const normalizeDiscoveryApiParams = (params) => {
    const listingAndDiscoveryFieldsMap = {
        _sort: 'sort',
        _order: 'order',
        bestPrice_gte: 'priceGte',
        bestPrice_lte: 'priceLte',
        price_lte: 'priceLte',
        price_gte: 'priceGte',
        _limit: 'itemsPerPage',
        limit: 'itemsPerPage',
        sellerCategories: 'masterCategoryIds',
        _page: 'pageNumber',
        page: 'pageNumber',
        q: 'query',
        keyword: 'query',
        skus_ne: 'excludedSkus',
        providerId: 'providerIds',
    };
    const normalizedParams = {};
    Object.keys(params).forEach((field) => {
        if (listingAndDiscoveryFieldsMap[field]) {
            normalizedParams[listingAndDiscoveryFieldsMap[field]] = params[field];
        }
        else {
            normalizedParams[field] = params[field];
        }
    });
    return normalizedParams;
};
const convertArrayValue = (value, returnType = 'string') => {
    if (typeof value === 'string') {
        if (returnType === 'string')
            return value.split(',');
        return value.split(',').map((el) => +el);
    }
    if (returnType === 'string')
        return value;
    return value.map((el) => +el);
};
class DiscoveryService {
    constructor(discoveryApiUrl, ecomApiUrl = '/api', language) {
        this._isArrayValue = [
            'sellerCodes',
            'brands',
            'categories',
            'tags',
            'providerIds',
            'sellerIds',
            'skus',
            'masterCategories',
            'masterCategoryIds',
            'excludedSkus',
            'returnFilterable',
            'excludedSkus',
            'clearanceTypeIds',
        ];
        this._discoveryPayloadKeys = [
            'terminalCode',
            'query',
            'filter',
            'pagination',
            'sorting',
            'userId',
            'phoneNumber',
            'location',
            'returnFilterable',
            'block',
            'pos365StoreCode',
        ];
        this._discoveryBlockKeys = ['blockId', 'itemId'];
        this._discoveryFilterKeys = [
            'sellingCodes',
            'priceLte',
            'priceGte',
            'brands',
            'categories',
            'tags',
            'hasPromotions',
            'attributes',
            'providerIds',
            'sellerIds',
            'skus',
            'flashSaleType',
            'masterCategories',
            'masterCategoryIds',
            'excludedSkus',
            'clearanceTypeIds',
        ];
        this._isBooleanValue = ['hasPromotions'];
        this._isNumberValue = ['clearanceTypeIds'];
        this._discoverySortingKeys = ['sort', 'order'];
        this._discoveryPaginationKeys = ['pageNumber', 'itemsPerPage'];
        this._orderValuesMap = {
            desc: 'ORDER_BY_DESCENDING',
            asc: 'ORDER_BY_ASCENDING',
            DESC: 'ORDER_BY_DESCENDING',
            ASC: 'ORDER_BY_ASCENDING',
        };
        this._sortValuesMap = {
            new: 'SORT_BY_PUBLISH_AT',
            bestPrice: 'SORT_BY_PRICE',
            'view.last_3_day': 'SORT_BY_MOST_VIEW',
            'view.last_7_day': 'SORT_BY_MOST_VIEW_7_DAYS',
            'view.last_30_day': 'SORT_BY_MOST_VIEW_30_DAYS',
            'view.last_90_day': 'SORT_BY_MOST_VIEW_90_DAYS',
            'quantity.last_3_day': 'SORT_BY_TOP_SALE_QUANTITY',
            'quantity.last_1_week': 'SORT_BY_TOP_SALE_QUANTITY_7_DAYS',
            'quantity.last_1_month': 'SORT_BY_TOP_SALE_QUANTITY_30_DAYS',
            'quantity.last_3_month': 'SORT_BY_TOP_SALE_QUANTITY_90_DAYS',
            discountPercent: 'SORT_BY_DISCOUNT_PERCENT',
            discount: 'SORT_BY_DISCOUNT_PERCENT',
        };
        this.discoveryApi = new Api(discoveryApiUrl);
        this.discoveryApi.addInterceptor((request) => {
            request.headers.set('Accept-Language', language || '*');
        });
        this.serverApi = new Api(ecomApiUrl);
    }
    _cookPayload(params) {
        const payload = {
            filter: {},
            pagination: {},
            query: '',
            sorting: {},
            returnFilterable: [],
            terminalCode: '',
            block: {},
        };
        const normalizedParams = normalizeDiscoveryApiParams(normalizePageBuilderToDiscoveryParams(params));
        const attributes = [];
        Object.keys(normalizedParams).forEach((key) => {
            let data = this._isArrayValue.includes(key)
                ? convertArrayValue(normalizedParams[key], this._isNumberValue.includes(key) ? 'number' : 'string')
                : normalizedParams[key];
            if (key.startsWith('attributes.')) {
                attributes.push({
                    code: key.replace('attributes.', ''),
                    optionIds: data.split(','),
                });
            }
            if (this._isBooleanValue.includes(key)) {
                data = data === 'true' || data === true;
            }
            if (this._discoveryPayloadKeys.includes(key)) {
                payload[key] = data;
            }
            if (this._discoveryBlockKeys.includes(key)) {
                payload.block[key] = data;
            }
            if (this._discoveryFilterKeys.includes(key)) {
                payload.filter = Object.assign(Object.assign({}, payload.filter), { [key]: data });
            }
            if (this._discoverySortingKeys.includes(key)) {
                payload.sorting = Object.assign(Object.assign({}, payload.sorting), {
                    [key]: key === 'order'
                        ? this._orderValuesMap[data] || data || 'ORDER_BY_UNSPECIFIED'
                        : this._sortValuesMap[data] || data || 'SORT_BY_UNSPECIFIED',
                });
            }
            if (this._discoveryPaginationKeys.includes(key)) {
                payload.pagination = Object.assign(Object.assign({}, payload.pagination), {
                    [key]: data,
                });
            }
        });
        payload['filter']['attributes'] = [
            ...(payload['filter']['attributes'] || []),
            ...attributes,
        ];
        return payload;
    }
    getBaseUrl() {
        return this.discoveryApi.baseURL;
    }
    async search(params) {
        const searchParams = this._cookPayload(params);
        const { data } = await this.discoveryApi.post('/search', searchParams);
        return data;
    }
    async getProductDetailBySkus(params) {
        var _a;
        const { data } = await this.discoveryApi.get('/products', {
            params,
        });
        return ((_a = data === null || data === void 0 ? void 0 : data.result) === null || _a === void 0 ? void 0 : _a.products) || [];
    }
    async getProduct(params, callFromDiscovery = true) {
        const { sku, terminalCode, location } = params;
        let apiParams = null;
        let api = this.serverApi;
        if (callFromDiscovery) {
            api = this.discoveryApi;
            apiParams = {
                terminalCode,
                location,
            };
        }
        const { data } = await api.get(`/product/${sku}`, {
            params: apiParams,
        });
        return data;
    }
    async getKeyword(params) {
        const { data } = await this.discoveryApi.get('/keywords', {
            params,
        });
        return data;
    }
    async getBlock(params) {
        const { data } = await this.discoveryApi.get(`/blocks`, {
            params,
        });
        return data;
    }
    async getPopularKeyword(params) {
        const { data } = await this.discoveryApi.get(`/recommended-search-terms`, {
            params,
        });
        return data;
    }
    async getRecommendationProducts(sku, terminalCode) {
        const { data } = await this.discoveryApi.get(`/products/${sku}/recommendations`, {
            params: { terminalCode },
        });
        return data;
    }
    async getRecommendationCombos(sku, params) {
        const { data } = await this.discoveryApi.get(`/products/${sku}/combos`, {
            params,
        });
        return data;
    }
    async getDetailComboRecommendation(params) {
        const { data } = await this.discoveryApi.post(`/combos`, params);
        return data;
    }
    async getMenuItems(terminal, zone) {
        const result = await this.discoveryApi.get(`/menu-items`, {
            params: {
                terminal,
                zone: zone,
                _limit: 1000,
                _sort: 'priority',
                _order: 'asc',
            },
        });
        return result.data;
    }
    async getMenuItemsZonePrefix(terminal, zonePrefix) {
        const result = await this.discoveryApi.get(`/menu-items`, {
            params: {
                terminal,
                zonePrefix,
                _limit: 10000,
                _sort: 'priority',
                _order: 'asc',
            },
        });
        return result.data;
    }
    async getMenuItemById(categoryId) {
        const { data } = await this.discoveryApi.get(`/menu-item/${categoryId}`);
        return data;
    }
}

class Sorting {
}
class BlockAttribute {
}
var PageTypeEnum;
(function (PageTypeEnum) {
    PageTypeEnum["HOME"] = "PAGE_TYPE_HOME";
    PageTypeEnum["CATEGORY"] = "PAGE_TYPE_CATEGORY";
    PageTypeEnum["NOTIFICATION"] = "PAGE_TYPE_NOTIFICATION";
    PageTypeEnum["SELLER"] = "PAGE_TYPE_SELLER";
    PageTypeEnum["BRAND"] = "PAGE_TYPE_BRAND";
})(PageTypeEnum || (PageTypeEnum = {}));

const paramToUrl = (params, baseUrl, key) => {
    const url = new URL(`${baseUrl}/${key || 'search'}`);
    if (params) {
        Object.keys(params).forEach((key) => {
            if (Array.isArray(params[key]) && !params[key].length)
                return;
            return url.searchParams.append(key, params[key]);
        });
    }
    return url;
};
const getObjFromQueryString = (str) => {
    const urlParams = new URLSearchParams(str);
    const result = {};
    for (const pair of urlParams.entries()) {
        result[pair[0]] = pair[1];
    }
    return result;
};
const cookFilterOption = (el, filterCode, filterName, params) => {
    var _a, _b;
    const optionName = el.name || el.value;
    const optionCode = el.code || el.optionId || ((_a = el.id) === null || _a === void 0 ? void 0 : _a.toString());
    if (!params ||
        !((_b = params[filterCode]) === null || _b === void 0 ? void 0 : _b.toString().split(',').includes(optionCode)))
        return {
            optionName,
            optionCode,
            filterCode,
            filterName,
        };
};
const cookFilter = (filter, params) => {
    var _a;
    const newFilter = [];
    if (!filter)
        return newFilter;
    if (filter.brands.length) {
        newFilter.push({
            code: 'brands',
            name: 'Thương hiệu',
            options: filter.brands
                .map((val) => cookFilterOption(val, 'brands', 'Thương hiệu', params))
                .filter(Boolean),
        });
    }
    if ((_a = filter === null || filter === void 0 ? void 0 : filter.clearanceTypes) === null || _a === void 0 ? void 0 : _a.length) {
        newFilter.push({
            code: 'clearanceTypeIds',
            name: 'Tình trạng sản phẩm',
            options: filter.clearanceTypes
                .map((val) => cookFilterOption(val, 'clearanceTypeIds', 'Tình trạng sản phẩm', params))
                .filter(Boolean),
        });
    }
    filter.attributes.forEach((el) => {
        newFilter.push({
            code: `attributes.${el.code}`,
            name: el.name,
            options: el.values
                .map((val) => cookFilterOption(val, `attributes.${el.code}`, el.name, params))
                .filter(Boolean),
        });
    });
    return newFilter.filter((filter) => !!filter.options.length);
};
const cookSearchIntent = (result) => {
    if (!result)
        return null;
    const { brands = [], sellers = [], masterCategories = [] } = result;
    const cookedIntentSellers = sellers.map((el) => ({
        id: el.id,
        type: PageTypeEnum.SELLER,
        text: el.displayName,
        logo: el.logo,
    }));
    const cookedIntentBrands = brands.map((el) => ({
        id: el.code,
        type: PageTypeEnum.BRAND,
        text: el.name,
        logo: el.logo,
    }));
    const cookedIntentCategories = masterCategories.map((el) => ({
        id: el.id,
        type: PageTypeEnum.CATEGORY,
        text: el.name,
    }));
    return {
        sellers: cookedIntentSellers,
        others: [...cookedIntentBrands, ...cookedIntentCategories],
    };
};

const useDiscoverySearch = (discoveryService, params, location, isSearchNoKeyword) => {
    const _a = params || {}, { sorting } = _a, searchParams = __rest(_a, ["sorting"]);
    const normalizedParams = normalizePageBuilderToDiscoveryParams(searchParams);
    const url = paramToUrl(normalizedParams, discoveryService.getBaseUrl());
    const terminalCode = normalizedParams === null || normalizedParams === void 0 ? void 0 : normalizedParams.terminalCode;
    const pos365StoreCode = normalizedParams === null || normalizedParams === void 0 ? void 0 : normalizedParams.pos365StoreCode;
    const { data, error } = useSWR(params && (!!terminalCode || !pos365StoreCode)
        ? [`${url.href}`, url.search]
        : null, async (_url, params) => {
        return await discoveryService.search(Object.assign(Object.assign({}, getObjFromQueryString(params)), { location,
            sorting }));
    });
    const result = data === null || data === void 0 ? void 0 : data.result;
    return {
        result: {
            products: (result === null || result === void 0 ? void 0 : result.products) || [],
            filter: (result === null || result === void 0 ? void 0 : result.filter) || {},
            pagination: (data === null || data === void 0 ? void 0 : data.pagination) || {},
            cookedFilters: cookFilter(result === null || result === void 0 ? void 0 : result.filter, isSearchNoKeyword ? normalizedParams : undefined),
            searchIntents: cookSearchIntent(result),
            suggestedKeyword: result === null || result === void 0 ? void 0 : result.suggestedKeyword,
        },
        isLoading: !params ? false : !error && !data,
        isError: error,
    };
};
const useInfiniteDiscoverySearch = (discoveryService, params, location) => {
    const url = paramToUrl(params, discoveryService.getBaseUrl());
    const { data, error, size = 1, setSize = () => { }, } = useSWRInfinite((index) => [
        `${url.href}&_page=${index + 1}`,
        `${url.search}&_page=${index + 1}`,
    ], async (url, params) => {
        var _a, _b;
        const res = await discoveryService.search(Object.assign(Object.assign({}, getObjFromQueryString(params)), { location }));
        return {
            products: ((_a = res === null || res === void 0 ? void 0 : res.result) === null || _a === void 0 ? void 0 : _a.products) || [],
            searchIntents: cookSearchIntent(res === null || res === void 0 ? void 0 : res.result),
            suggestedKeyword: (_b = res === null || res === void 0 ? void 0 : res.result) === null || _b === void 0 ? void 0 : _b.suggestedKeyword,
        };
    });
    const isLoading = !data && !error;
    const isEmpty = data && data[0].products.length === 0;
    const hasMore = !(isEmpty ||
        (data &&
            data[data.length - 1] &&
            data[data.length - 1].products.length < (params._limit || 10)));
    const products = data
        ? data.reduce((acc, curr) => {
            return [...acc, ...curr.products];
        }, [])
        : [];
    const cookedData = data === null || data === void 0 ? void 0 : data[0];
    return {
        isEmpty,
        isLoading,
        hasMore,
        page: size,
        setPage: setSize,
        products,
        searchIntents: (cookedData === null || cookedData === void 0 ? void 0 : cookedData.searchIntents) ? cookedData.searchIntents : null,
        suggestedKeyword: cookedData === null || cookedData === void 0 ? void 0 : cookedData.suggestedKeyword,
    };
};
const useDiscoveryProduct = (discoveryService, sku, terminal) => {
    var _a;
    const { data, error } = useSWR(!!sku && !!terminal
        ? [`${discoveryService.getBaseUrl}/product`, sku, terminal]
        : null, async (_, sku, terminal) => {
        return await discoveryService.getProduct({ sku, terminalCode: terminal }, false);
    });
    return {
        result: {
            product: ((_a = data === null || data === void 0 ? void 0 : data.result) === null || _a === void 0 ? void 0 : _a.product) || null,
        },
        isLoading: !error && !data,
        isError: !!error,
    };
};
const usePersonalizeBlocks = (discoveryService, blockParams, terminal, transformBlock, user) => {
    var _a;
    const { pageTypeEnum, category, seller, brand, sellerCategory } = blockParams;
    const phoneNumber = user === null || user === void 0 ? void 0 : user.telephone;
    const userId = user === null || user === void 0 ? void 0 : user.userId;
    const params = cleanObject(Object.assign({ terminalCode: terminal, phoneNumber,
        userId }, blockParams));
    const conditionFetching = terminal &&
        (pageTypeEnum === PageTypeEnum.HOME ||
            PageTypeEnum.NOTIFICATION ||
            (pageTypeEnum === PageTypeEnum.CATEGORY &&
                (category || sellerCategory)) ||
            (pageTypeEnum === PageTypeEnum.BRAND && brand) ||
            (pageTypeEnum === PageTypeEnum.SELLER && seller));
    const { data, error } = useSWR(conditionFetching
        ? [`${discoveryService.getBaseUrl}/blocks`, JSON.stringify(params)]
        : null, async (_, paramsJson) => {
        return await discoveryService.getBlock(JSON.parse(paramsJson));
    });
    return {
        result: (((_a = data === null || data === void 0 ? void 0 : data.result) === null || _a === void 0 ? void 0 : _a.blocks) || []).map(transformBlock),
        isLoading: !error && !data,
        isError: error,
    };
};
const useSkus = (discoveryService, skus, terminal, location) => {
    const { data, error } = useSWR(skus
        ? [
            `${discoveryService.getBaseUrl}/products`,
            skus.join(','),
            terminal,
            location,
        ]
        : null, async (_, skus, terminal, location) => {
        return await discoveryService.getProductDetailBySkus({
            skus,
            terminalCode: terminal,
            location,
        });
    });
    return {
        result: {
            products: data || [],
        },
        isLoading: !skus ? false : !error && !data,
        isError: error,
    };
};

const getProductUrl = (name, sku) => {
    return `/${slug(name)}--s${sku}?sku=${sku}`;
};

export { BlockAttribute, DiscoveryService, PageTypeEnum, Sorting, getProductUrl, normalizePageBuilderToDiscoveryParams, useDiscoveryProduct, useDiscoverySearch, useInfiniteDiscoverySearch, usePersonalizeBlocks, useSkus };
